/**
 * DashboardPage Component
 *
 * Main dashboard overview showing summary stats and quick actions.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout, BookingCard } from './components';
import { useMyBookings, useNotifications } from './hooks';
import { useAuth } from '../../contexts';

export const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { bookings, isLoading, stats } = useMyBookings('upcoming');
  const { unreadCount: unreadNotifications } = useNotifications();

  return (
    <DashboardLayout>
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Welcome back, {user?.displayName?.split(' ')[0] || 'Guest'}!
        </h1>
        <p className="text-gray-600">
          Here's what's happening with your trips and account.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Upcoming Trips"
          value={stats.upcoming}
          icon={<CalendarIcon className="w-5 h-5" />}
          href="/dashboard/bookings?tab=upcoming"
          color="primary"
        />
        <StatCard
          label="Total Bookings"
          value={stats.total}
          icon={<BookIcon className="w-5 h-5" />}
          href="/dashboard/bookings"
          color="blue"
        />
        <StatCard
          label="Notifications"
          value={unreadNotifications}
          icon={<BellIcon className="w-5 h-5" />}
          href="/dashboard/notifications"
          color="amber"
          highlight={unreadNotifications > 0}
        />
        <StatCard
          label="Reviews to Leave"
          value={stats.needsReview}
          icon={<StarIcon className="w-5 h-5" />}
          href="/dashboard/bookings?tab=past"
          color="green"
          highlight={stats.needsReview > 0}
        />
      </div>

      {/* Upcoming Bookings */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Upcoming Trips</h2>
          <Link
            to="/dashboard/bookings"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            View all
          </Link>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="animate-pulse bg-gray-100 rounded-xl h-32" />
            ))}
          </div>
        ) : bookings.length > 0 ? (
          <div className="space-y-4">
            {bookings.slice(0, 3).map((booking) => (
              <BookingCard key={booking.id} booking={booking} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<CalendarIcon className="w-12 h-12" />}
            title="No upcoming trips"
            description="When you book a property, your upcoming trips will appear here."
            action={
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                <SearchIcon className="w-4 h-4" />
                Start exploring
              </Link>
            }
          />
        )}
      </section>

      {/* Quick Actions */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <QuickActionCard
            icon={<SearchIcon className="w-6 h-6" />}
            title="Search Properties"
            description="Find your next getaway in Lebanon"
            href="/"
          />
          <QuickActionCard
            icon={<HeartIcon className="w-6 h-6" />}
            title="Saved Properties"
            description="View your saved favorites"
            href="/dashboard/saved"
          />
          <QuickActionCard
            icon={<MessageIcon className="w-6 h-6" />}
            title="Messages"
            description="Chat with hosts"
            href="/dashboard/messages"
          />
        </div>
      </section>
    </DashboardLayout>
  );
};

// Stat Card
interface StatCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  href: string;
  color: 'primary' | 'blue' | 'amber' | 'green';
  highlight?: boolean;
}

const colorClasses = {
  primary: 'bg-primary-50 text-primary-600',
  blue: 'bg-blue-50 text-blue-600',
  amber: 'bg-amber-50 text-amber-600',
  green: 'bg-green-50 text-green-600',
};

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon,
  href,
  color,
  highlight,
}) => (
  <Link
    to={href}
    className={`p-4 rounded-xl border transition-shadow hover:shadow-md ${
      highlight ? 'border-primary-200 bg-primary-50' : 'border-gray-200 bg-white'
    }`}
  >
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${colorClasses[color]}`}>
      {icon}
    </div>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    <p className="text-sm text-gray-500">{label}</p>
  </Link>
);

// Quick Action Card
interface QuickActionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  href: string;
}

const QuickActionCard: React.FC<QuickActionCardProps> = ({
  icon,
  title,
  description,
  href,
}) => (
  <Link
    to={href}
    className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-shadow"
  >
    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600">
      {icon}
    </div>
    <div>
      <h3 className="font-medium text-gray-900">{title}</h3>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
  </Link>
);

// Empty State
interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
}) => (
  <div className="text-center py-12 px-4 bg-gray-50 rounded-xl">
    <div className="text-gray-300 mb-4 flex justify-center">{icon}</div>
    <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 mb-6 max-w-sm mx-auto">{description}</p>
    {action}
  </div>
);

// Icons
const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const BookIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
    />
  </svg>
);

const BellIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />
  </svg>
);

const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
    />
  </svg>
);

const SearchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

const HeartIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
    />
  </svg>
);

const MessageIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
    />
  </svg>
);

export default DashboardPage;
